import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Loader2, Plus, Trash2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { generateInvoicePdfBase64 } from '@/lib/generateInvoicePdf';
import { invoiceAgingDays } from '@/lib/invoiceHelpers';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Confirm-before-send modal for the Invoices tab's Email button, and for
// its "Remind" button (mode='reminder') on invoices unpaid 30+ days.
// Previously that button sent immediately to whatever customer_email
// happened to be on file -- no chance to catch a wrong/stale address, and
// no way to loop in a second person on the broker's side (e.g. AP plus the
// dispatcher who booked the load) without leaving the app to forward it
// manually. Both modes now also attach a real invoice PDF (generated
// client-side with jsPDF), not just an HTML email body.
export default function EmailInvoiceModal({ invoice, onClose, onSent, mode = 'invoice' }) {
  const isReminder = mode === 'reminder';
  const agingDays = invoiceAgingDays(invoice);
  const initial = [invoice.customer_email, ...(invoice.customer_email_cc || [])].filter(Boolean);
  const [recipients, setRecipients] = useState(initial.length > 0 ? initial : ['']);
  const [sending, setSending] = useState(false);

  const setRecipient = (i, value) => setRecipients(prev => prev.map((r, idx) => idx === i ? value : r));
  const addRecipient = () => setRecipients(prev => [...prev, '']);
  const removeRecipient = (i) => setRecipients(prev => prev.filter((_, idx) => idx !== i));

  const handleSend = async () => {
    const cleaned = [...new Set(recipients.map(r => r.trim()).filter(Boolean))];
    if (cleaned.length === 0) { toast.error('Enter at least one email address.'); return; }
    const invalid = cleaned.filter(r => !EMAIL_RE.test(r));
    if (invalid.length > 0) { toast.error(`Invalid email address: ${invalid.join(', ')}`); return; }

    setSending(true);
    try {
      const companySettings = await client.entities.CompanySettings.list().catch(() => []);
      const companyName = companySettings.find(s => s.key === 'company_name')?.value || 'Pro Haul';

      const reminderIntro = agingDays != null
        ? `<p>This is a friendly reminder that Invoice <strong>${invoice.invoice_number}</strong> is <strong>${agingDays} days past due</strong> and remains unpaid. The invoice is attached again for your records — please arrange payment at your earliest convenience.</p>`
        : `<p>This is a friendly reminder that Invoice <strong>${invoice.invoice_number}</strong> remains unpaid. The invoice is attached again for your records — please arrange payment at your earliest convenience.</p>`;
      const body = `
        <p>Hi ${invoice.customer_name || 'there'},</p>
        ${isReminder ? reminderIntro : '<p>Please find your invoice attached, with the details below.</p>'}
        <table style="border-collapse:collapse;margin:12px 0;">
          <tr><td style="padding:3px 16px 3px 0;color:#666;">Invoice #</td><td style="padding:3px 0;font-weight:bold;">${invoice.invoice_number}</td></tr>
          ${invoice.load_number ? `<tr><td style="padding:3px 16px 3px 0;color:#666;">Load #</td><td style="padding:3px 0;">${invoice.load_number}</td></tr>` : ''}
          <tr><td style="padding:3px 16px 3px 0;color:#666;">Invoice Date</td><td style="padding:3px 0;">${invoice.invoiced_date || '—'}</td></tr>
          <tr><td style="padding:3px 16px 3px 0;color:#666;">Total Amount</td><td style="padding:3px 0;font-weight:bold;">$${(invoice.total_amount || 0).toLocaleString()}</td></tr>
          <tr><td style="padding:3px 16px 3px 0;color:#666;">Balance Due</td><td style="padding:3px 0;font-weight:bold;color:#c0392b;">$${(invoice.balance_due || 0).toLocaleString()}</td></tr>
        </table>
        ${invoice.notes ? `<p>${invoice.notes}</p>` : ''}
        <p>Thank you for your business.<br/>${companyName}</p>
      `.trim();

      // An invoice email without the invoice attached is useless to the
      // broker -- fail loudly rather than quietly sending text only.
      // Pull the load so the PDF itemizes line haul and accessorial fees.
      // If the lookup fails the PDF still generates, just with one lump line.
      const load = invoice.load_id
        ? (await client.entities.Load.filter({ id: invoice.load_id }).catch(() => []))[0] || null
        : null;
      const pdfBase64 = await generateInvoicePdfBase64(invoice, companyName, load).catch(err => {
        console.error('Invoice PDF generation failed:', err);
        return null;
      });
      if (!pdfBase64) throw new Error('Could not generate the invoice PDF, so nothing was sent. Try again.');

      await client.integrations.Core.SendEmail({
        to: cleaned,
        subject: `${isReminder ? 'Payment Reminder — ' : ''}Invoice ${invoice.invoice_number}${invoice.load_number ? ` — Load #${invoice.load_number}` : ''} — ${companyName}`,
        body,
        attachments: [{ filename: `Invoice-${invoice.invoice_number}.pdf`, content: pdfBase64 }],
      });
      // Remember whatever the user confirmed/changed here so the next send
      // to this invoice starts from the same recipient list. A manual
      // reminder also resets last_reminder_sent_at/reminder_count so the
      // automated 30-day reminder job (sendInvoiceReminders) doesn't send
      // its own reminder again right on top of this one.
      await client.entities.Invoice.update(invoice.id, {
        customer_email: cleaned[0],
        customer_email_cc: cleaned.slice(1),
        ...(isReminder ? {
          last_reminder_sent_at: new Date().toISOString(),
          reminder_count: (invoice.reminder_count || 0) + 1,
        } : {}),
      }).catch(() => {}); // non-fatal -- the email itself already went out
      toast.success(`${isReminder ? 'Reminder' : 'Invoice'} emailed to ${cleaned.length} recipient${cleaned.length !== 1 ? 's' : ''}.`);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Failed to send invoice email.');
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-md p-6 space-y-4 z-10">
        <div className="flex items-center justify-between">
          <h2 className="font-space font-semibold text-foreground flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" /> {isReminder ? 'Send Payment Reminder' : 'Email Invoice'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="text-xs text-muted-foreground space-y-0.5">
          <p>Invoice <span className="text-foreground font-semibold">{invoice.invoice_number}</span> — {invoice.customer_name}</p>
          {invoice.load_number && <p>Load #{invoice.load_number}</p>}
          <p>Total: <span className="text-foreground font-semibold">${(invoice.total_amount || 0).toLocaleString()}</span> — Balance Due: <span className="text-foreground font-semibold">${(invoice.balance_due || 0).toLocaleString()}</span></p>
          {isReminder && agingDays != null && (
            <p className="text-amber-500 font-medium">{agingDays} days past due</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-xs text-muted-foreground block">Send To</label>
          {recipients.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="email"
                value={r}
                onChange={e => setRecipient(i, e.target.value)}
                placeholder="billing@broker.com"
                className={inputCls}
                autoFocus={i === 0}
              />
              {recipients.length > 1 && (
                <button onClick={() => removeRecipient(i)} className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          <button onClick={addRecipient} className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors font-medium">
            <Plus className="w-3.5 h-3.5" /> Add recipient
          </button>
        </div>

        <div className="flex gap-3 pt-1">
          <Button onClick={handleSend} disabled={sending} className="flex-1 bg-primary text-primary-foreground">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : (isReminder ? 'Send Reminder' : 'Send Invoice')}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-border">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
